import styled from 'styled-components';
import { SectionBase } from '../styles';

export const ScrollContainer = styled(SectionBase)`
	padding-top: 0;
`;

export const HeaderArea = styled.div`
	padding-top: 42px;
`;

export const CategorySection = styled.section`
	margin-bottom: 40px;

	&:last-child {
		margin-bottom: 0;
	}
`;

export const BandHeader = styled.h4`
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[700]};
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin: 16px 0 8px 0;
	padding-bottom: 6px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const RecipeDomainSection = styled.section`
	margin-bottom: 32px;
`;

export const DomainHeader = styled.h3`
	font-size: 16px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[900]};
	margin: 24px 0 12px 0;
	text-transform: capitalize;

	&:first-of-type {
		margin-top: 0;
	}
`;

export const RecipeGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: 16px;
	width: 100%;
`;

export const RecipeCard = styled.div<{ $selected?: boolean }>`
	position: relative;
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[200])};
	border-radius: 8px;
	transition: all 0.2s ease;
	display: flex;
	flex-direction: column;

	&:hover {
		border-color: ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[400])};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.blue[500]};
		outline-offset: 2px;
	}
`;

// Visual preview area at the top of each recipe card. Centered with comfortable
// padding so box-shadows / borders on the previewed element have room to render.
// Top-corner radius matches the card so the gray fill doesn't bleed past the
// card's rounded corners (the card itself can't use overflow:hidden because that
// would clip the hover tooltip rendered outside the card boundary).
export const RecipePreviewArea = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 110px;
	padding: 20px;
	background-color: ${({ theme }) => theme.gray[100]};
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	border-top-left-radius: 7px;
	border-top-right-radius: 7px;
`;

// The styled element itself — recipe properties are applied as inline styles.
// `$forceSize` is set for container-like recipes (no padding / font-size / width
// / height of their own) so the surface, border, and shadow are actually
// visible; without it, an empty <div> collapses to a sliver.
export const RecipePreviewElement = styled.div<{ $forceSize?: boolean }>`
	max-width: 100%;
	box-sizing: border-box;
	overflow: hidden;
	text-overflow: ellipsis;

	${({ $forceSize }) =>
		$forceSize
			? `
				min-width: 180px;
				min-height: 70px;
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 12px 16px;
			`
			: ''}
`;

export const RecipeMeta = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 12px 16px 14px;
`;

export const RecipeHeader = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	justify-content: space-between;
`;

export const RecipeName = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[1000]};
`;

export const RecipeClassName = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

export const RecipeDescription = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[700]};
	line-height: 1.4;
`;

// Small tooltip-style popover that reveals the full CSS on card hover. Matches
// the SpecimenTooltip pattern used elsewhere — does NOT replace the whole card.
export const RecipeTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 16px;
	transform: translateY(-6px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 10px 14px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: pre;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 20;
	max-width: min(420px, calc(100vw - 80px));
	line-height: 1.5;
	box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);

	${RecipeCard}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 24px;
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;

export const SubSectionTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[900]};
	margin: 32px 0 16px 0;
`;
